/**
 * SegmentedControl — a horizontal row of toggle buttons styled as a single
 * pill, with the active option filled in an accent color.
 *
 * Originally collapsed the three byte-identical nowcasts selectors (Timeframe,
 * Inflation Type, GDP View). It now also subsumes two other "row of toggle
 * buttons" widgets that were structurally close enough to fold in:
 *   - `mars-forecasts/PillGroup`        → `variant="pill"`
 *   - `mars-portfolio` `PillButtonGroup` → `variant="group"` / `"group-secondary"`
 *
 * Each `variant` is a self-contained preset of wrapper/container/button classes
 * reproduced byte-for-byte from the original component, so migrated call sites
 * render identical markup (zero pixel change). The `dense` flag is the one
 * intra-variant tweak: the `default` Period row used `py-2`, the Type / View
 * rows used `py-2.5`.
 *
 * The timeseries `PeriodButtons` is intentionally NOT folded in: it uses an
 * overlapping-border model (`-ml-px first:ml-0`, per-button rounded corners,
 * full `border` per button, focus-visible rings) that would require
 * restructuring this component's shared-border container.
 */
import type { CSSProperties } from 'react';

export type SegmentedControlVariant = 'default' | 'pill' | 'group' | 'group-secondary';

export interface SegmentedControlProps<V extends string> {
  /** Ordered options; each rendered as one button. */
  options: ReadonlyArray<{ value: V; label: string }>;
  /** Currently selected value. */
  value: V;
  /** Fired with the clicked option's value. */
  onChange: (value: V) => void;
  /** Optional inline label rendered before the button row (e.g. "Period:"). */
  label?: string;
  /** Values that should render disabled / non-interactive. */
  disabledValues?: ReadonlyArray<V>;
  /** Accessible name for the radiogroup. Defaults to `label` (sans colon). */
  ariaLabel?: string;
  /** Use the tighter `py-2` padding (timeframe row); default `py-2.5`. Only
   *  affects the `default` variant. */
  dense?: boolean;
  /** Visual preset; see the module doc-comment. Defaults to `default`. */
  variant?: SegmentedControlVariant;
}

/**
 * Per-variant class bundles. Each field reproduces the exact class string of
 * the original component so migrated call sites render a byte-identical `class`
 * attribute. `buildButtonClass` assembles them in the original token order.
 */
interface VariantStyle {
  /** Outer flex row wrapper classes. */
  wrapper: string;
  /** Label classes (the `<label>` / `<span>` before the button row). */
  label: string;
  /** Inline style applied to the button container (box-shadows that Tailwind
   *  cannot express identically). */
  containerStyle?: CSSProperties;
  /** Button container classes. */
  container: string;
  /**
   * Assembles the per-button class string in the original token order, given
   * the button's active / inactive / disabled / position state.
   */
  buildButtonClass: (state: {
    active: boolean;
    disabled: boolean;
    isLast: boolean;
    dense: boolean;
  }) => string;
}

function join(...parts: Array<string | false | undefined>): string {
  return parts.filter(Boolean).join(' ');
}

const VARIANTS: Record<SegmentedControlVariant, VariantStyle> = {
  // Nowcasts Timeframe / Inflation Type / GDP View selectors.
  default: {
    wrapper: 'flex items-center gap-4',
    label:
      'whitespace-nowrap font-[var(--font-heading)] text-sm font-semibold text-gray-700',
    container: 'flex overflow-hidden rounded-lg border border-gray-200 shadow-sm',
    // Original token order: `flex-1 border-r ... px-3 ${padY} text-xs ... ${state}`.
    buildButtonClass: ({ active, disabled, dense }) =>
      join(
        `flex-1 border-r border-gray-200 px-3 ${dense ? 'py-2' : 'py-2.5'} text-xs font-medium transition-all duration-200 last:border-r-0`,
        active
          ? 'bg-[color:var(--accent)] font-semibold text-white shadow-[inset_0_1px_3px_rgba(0,0,0,0.1)]'
          : 'bg-white text-gray-500 hover:-translate-y-0.5 hover:bg-gray-50 hover:text-gray-700',
        disabled && 'cursor-not-allowed opacity-40',
      ),
  },
  // mars-forecasts PillGroup — connected rectangular buttons.
  pill: {
    wrapper: '',
    label: '',
    containerStyle: { boxShadow: '0 1px 2px 0 rgba(0,0,0,0.05)' },
    container:
      'flex overflow-hidden rounded-lg border border-[color:var(--gray-300)]',
    // Original built each button via an array joined in this exact order.
    buildButtonClass: ({ active, isLast }) =>
      join(
        'px-2 py-1 text-[0.8125rem] font-medium transition-colors cursor-pointer',
        'font-[var(--font-body)]',
        !isLast && 'border-r border-[color:var(--gray-300)]',
        active
          ? 'bg-[color:var(--accent)] text-white font-semibold'
          : 'bg-white text-[color:var(--gray-600)] hover:bg-[color:var(--gray-50)] hover:text-[color:var(--gray-800)]',
      ),
  },
  // mars-portfolio PillButtonGroup, accent variant.
  group: {
    wrapper: 'flex items-center gap-3',
    label: 'text-sm font-semibold text-[color:var(--text-dark)]',
    container: 'flex border-gray-200 border rounded-lg overflow-hidden shadow-sm',
    buildButtonClass: ({ active }) =>
      join(
        'px-3 py-2 text-[0.875rem] font-medium transition-all duration-200',
        active
          ? 'bg-[color:var(--accent)] text-white font-semibold'
          : 'bg-white text-[color:var(--text-dark)] hover:bg-gray-50',
      ),
  },
  // mars-portfolio PillButtonGroup, secondary variant.
  'group-secondary': {
    wrapper: 'flex items-center gap-3',
    label: 'text-sm font-semibold text-[color:var(--text-dark)]',
    container: 'flex border-slate-600 border rounded-lg overflow-hidden shadow-sm',
    buildButtonClass: ({ active }) =>
      join(
        'px-3 py-2 text-[0.875rem] font-medium transition-all duration-200',
        active
          ? 'bg-slate-700 text-white font-semibold'
          : 'bg-white text-slate-600 hover:bg-gray-50',
      ),
  },
};

export default function SegmentedControl<V extends string>({
  options,
  value,
  onChange,
  label,
  disabledValues,
  ariaLabel,
  dense = false,
  variant = 'default',
}: SegmentedControlProps<V>): JSX.Element {
  const groupLabel = ariaLabel ?? (label ? label.replace(/:\s*$/, '') : undefined);
  const style = VARIANTS[variant];

  return (
    <div className={style.wrapper} role="radiogroup" aria-label={groupLabel}>
      {label && <label className={style.label}>{label}</label>}
      <div className={style.container} style={style.containerStyle}>
        {options.map((opt, idx) => {
          const disabled = disabledValues?.includes(opt.value) ?? false;
          const active = opt.value === value;
          return (
            <button
              key={opt.value}
              type="button"
              role="radio"
              aria-checked={active}
              disabled={disabled}
              onClick={() => onChange(opt.value)}
              className={style.buildButtonClass({
                active,
                disabled,
                isLast: idx === options.length - 1,
                dense,
              })}
            >
              {opt.label}
            </button>
          );
        })}
      </div>
    </div>
  );
}
